import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { combatPowerApi } from '../lib/api.js';
import type { CombatPowerCustomViewInput } from '../types/combat-power.js';

/**
 * sheet 切替時に react-query の queryKey が変わるが、`placeholderData: keepPreviousData`
 * を使って前回データを保持する。これにより CombatPowerBlock がアンマウントされず、
 * 「+ 新Q追加」でクライアント側に積んだ `clientAddedQuarters` ローカル state が失われない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 戦闘力データ取得フック (sheet 切替時に前回データ保持)
 * @graph-connects /combat-power [calls] 戦闘力データ取得 API
 */
export function useCombatPower(sheet?: string) {
  return useQuery({
    queryKey: ['combat-power', sheet],
    queryFn: () => combatPowerApi.getData(sheet),
    placeholderData: keepPreviousData,
  });
}

/**
 * PJ Sandbox 等で使う、メンバー日次稼働割合 (稼働日計算タブ由来, 0=休息/>0=稼働) の取得フック。
 * 期間 [start, end] (YYYY-MM-DD) が両方揃ったときのみ fetch する。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー日次稼働割合の取得フック (稼働/休息日の可視化用)
 * @graph-connects /combat-power/daily-work-ratios [calls] メンバー日次稼働割合取得 API
 */
export function useDailyWorkRatios(start: string | undefined, end: string | undefined) {
  return useQuery({
    queryKey: ['combat-power', 'daily-work-ratios', start, end],
    queryFn: () => combatPowerApi.getDailyWorkRatios(start ?? '', end ?? ''),
    enabled: start != null && start !== '' && end != null && end !== '',
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー戦闘力履歴の取得フック
 * @graph-connects /combat-power/history [calls] 月度目標戦闘力履歴 API
 */
export function useCombatPowerHistory(name: string | undefined) {
  return useQuery({
    queryKey: ['combat-power', 'history', name],
    queryFn: () => combatPowerApi.getHistory(name ?? ''),
    enabled: name != null && name !== '',
  });
}

/**
 * 待処理要件定義pt（assign 済みで status=未対応 の要件定義セット子票）の取得フック。
 *
 * エンジニア看板の要件定義テーブルの「待処理要件定義pt」列・占有率列・明細モーダルの
 * 「待処理」節が同じ 1 レスポンスを使う（列の値と明細の合計を必ず一致させるため）。
 * 現在スナップショットで日次粒度の変化しかしないため、戦闘力データと同じ感覚の
 * staleTime (5 分) を持たせて全体ビューの再描画で叩き直さない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 待処理要件定義pt（担当者別の件数・予定pt・今日起点1ヶ月 capacity・占有率・明細）の取得フック
 * @graph-connects /combat-power/requirement-pending-resources [calls] 待処理要件定義pt 取得 API
 */
export function useRequirementPendingResources() {
  return useQuery({
    queryKey: ['combat-power', 'requirement-pending-resources'],
    queryFn: () => combatPowerApi.getRequirementPendingResources(),
    staleTime: 5 * 60 * 1000,
  });
}

/** カスタムビュー query key (取得・保存後の invalidate で共有) @graph-connects none */
const CUSTOM_VIEWS_KEY = ['combat-power', 'custom-views'] as const;

/**
 * PDG TE カスタムビュー (チーム共有) の取得フック。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 戦闘力カスタムビュー取得フック
 * @graph-connects /combat-power/custom-views [calls] カスタムビュー取得 API
 */
export function useCombatPowerCustomViews(enabled = true) {
  return useQuery({
    queryKey: CUSTOM_VIEWS_KEY,
    queryFn: () => combatPowerApi.getCustomViews(),
    enabled,
  });
}

/**
 * PDG TE カスタムビューの保存 (全置換) ミューテーション。成功時に取得 query を invalidate。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 戦闘力カスタムビュー保存フック (全置換)
 * @graph-connects /combat-power/custom-views [calls] カスタムビュー保存 API (PUT)
 */
export function useSaveCombatPowerCustomViews() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (views: CombatPowerCustomViewInput[]) => combatPowerApi.saveCustomViews(views),
    onSuccess: (data) => {
      queryClient.setQueryData(CUSTOM_VIEWS_KEY, data);
    },
  });
}
